import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2, Pencil, ExternalLink, ArrowLeft } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { getAdminGuides, createGuide, updateGuide, deleteGuide } from "@/lib/api";
import { COUNTRY_OPTIONS, isGeneral, flagProps } from "@/lib/countries";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  seo_title: "",
  seo_description: "",
  social_image: "",
  featured: false,
  published: false,
  category: "General",
};

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-ink font-medium">{label}</Label>
    {hint && <p className="text-xs text-ink-faint mb-1">{hint}</p>}
    {children}
  </div>
);

export const GuidesPanel = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, {} or guide = form
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setGuides((await getAdminGuides()).guides);
    } catch {
      toast.error("Couldn't load guides.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const startEdit = (g) => {
    setForm({ ...EMPTY, ...g, category: g.category || "General" });
    setEditing(g.id);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please add a title.");
    setSaving(true);
    try {
      if (editing === "new") {
        await createGuide(form);
        toast.success("Guide created.");
      } else {
        await updateGuide(editing, form);
        toast.success("Guide saved.");
      }
      await load();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save guide.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    try {
      await deleteGuide(g.id);
      toast.success("Guide deleted.");
      await load();
    } catch {
      toast.error("Couldn't delete guide.");
    }
  };

  return (
    <TabsContent value="guides" className="mt-6 space-y-6" data-testid="guides-panel">
      {editing === null ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-ink">Expert guides</h2>
            <Button data-testid="new-guide-button" onClick={startNew} className="bg-clay hover:bg-clay-hover text-white rounded-lg px-5 py-5">
              <Plus className="h-4 w-4 mr-2" /> New guide
            </Button>
          </div>

          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-clay" /></div>
          ) : guides.length === 0 ? (
            <p className="text-sm text-ink-muted">No guides yet. Create your first one.</p>
          ) : (
            <div className="bg-white border border-border rounded-2xl divide-y divide-border">
              {guides.map((g) => (
                <div key={g.id} className="flex items-center justify-between gap-4 p-4" data-testid={`guide-row-${g.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink truncate">{g.title}</span>
                      {!isGeneral(g.category) && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-soft bg-sand-deep px-1.5 py-0.5 rounded-full">
                          {flagProps(g.category) && (
                            <img {...flagProps(g.category)} width={14} height={11} alt="" className="rounded-[1px]" />
                          )}
                          {g.category}
                        </span>
                      )}
                      {g.featured && <span className="text-[10px] uppercase tracking-wide text-clay bg-clay/10 px-1.5 py-0.5 rounded-full">featured</span>}
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${g.published ? "text-forest bg-forest/10" : "text-ink-muted bg-sand-deep"}`}>
                        {g.published ? "published" : "draft"}
                      </span>
                    </div>
                    <div className="text-xs text-ink-faint mt-0.5">/guides/{g.slug}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {g.published && (
                      <a href={`/guides/${g.slug}`} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:text-ink" title="View">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <Button variant="ghost" size="sm" data-testid={`edit-guide-${g.slug}`} onClick={() => startEdit(g)} className="text-ink-muted hover:text-ink">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-guide-${g.slug}`} onClick={() => remove(g)} className="text-clay hover:text-clay-hover">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-5">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="guide-back">
            <ArrowLeft className="h-4 w-4" /> Back to guides
          </button>

          <div className="bg-white border border-border rounded-2xl p-8 space-y-5">
            <Field label="Title">
              <Input data-testid="guide-title-input" value={form.title} onChange={set("title")} placeholder="How to answer the 2026 UCAS questions" className="mt-1 bg-white border-border focus-visible:ring-clay rounded-xl py-6" />
            </Field>
            <Field label="URL slug" hint="Leave blank to auto-generate from the title. Lowercase, hyphenated.">
              <Input data-testid="guide-slug-input" value={form.slug} onChange={set("slug")} placeholder="how-to-answer-the-2026-ucas-questions" className="mt-1 bg-white border-border focus-visible:ring-clay rounded-xl py-6 font-mono text-sm" />
            </Field>
            <Field label="Summary / excerpt" hint="Shown in the guides list.">
              <textarea data-testid="guide-excerpt-input" value={form.excerpt} onChange={set("excerpt")} className="mt-1 w-full bg-white border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-xl p-4 text-sm resize-y min-h-[80px] outline-none" />
            </Field>

            <Field label="Category" hint="Choose 'General', or type a country (e.g. India, China). A country shows its flag on the guide card.">
              <Input
                list="guide-categories"
                data-testid="guide-category-input"
                value={form.category}
                onChange={set("category")}
                placeholder="General"
                className="mt-1 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
              />
              <datalist id="guide-categories">
                <option value="General" />
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>

            <Field label="Article body">
              <div className="mt-1">
                <RichTextEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} />
              </div>
            </Field>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="SEO page title" hint="The browser/Google title for this article.">
                <Input data-testid="guide-seotitle-input" value={form.seo_title} onChange={set("seo_title")} className="mt-1 bg-white border-border focus-visible:ring-clay rounded-xl py-6" />
              </Field>
              <Field label="Social preview image URL" hint="Shown when the link is shared.">
                <Input data-testid="guide-image-input" value={form.social_image} onChange={set("social_image")} placeholder="https://…" className="mt-1 bg-white border-border focus-visible:ring-clay rounded-xl py-6" />
              </Field>
            </div>
            <Field label="SEO meta description" hint="The snippet shown in search results & shares.">
              <textarea data-testid="guide-seodesc-input" value={form.seo_description} onChange={set("seo_description")} className="mt-1 w-full bg-white border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-xl p-4 text-sm resize-y min-h-[70px] outline-none" />
            </Field>

            <div className="flex flex-wrap items-center gap-8 pt-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="guide-featured-toggle" />
                <span className="text-sm text-ink">Featured</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="guide-published-toggle" />
                <span className="text-sm text-ink">Published {form.published ? "" : "(draft — hidden)"}</span>
              </label>
            </div>

            <div className="flex justify-end pt-2">
              <Button data-testid="save-guide-button" onClick={save} disabled={saving} className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-5 font-medium">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save guide"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  );
};
